import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useLanguageLabels } from '~/hooks/useLanguageLabels';
import type { LanguageCode } from '~/types/congregationGroupTypes';
import { loadGoogleMaps } from '~/utils/loadGoogleMaps';

export type TargetArea = {
  id: number;
  locationName: string;
  center: google.maps.LatLngLiteral;
  radiusMiles: number;
  languages: LanguageCode[];
  studentType: string;
  active?: boolean;
};

function radiusToZoom(radiusMiles: number): number {
  return Math.max(6, Math.round(14 - Math.log2(radiusMiles)));
}

export function TargetAreaMapCard({
  target,
  onEdit,
}: {
  target: TargetArea;
  onEdit?: () => void;
}) {
  const { t } = useTranslation();
  const languageLabels = useLanguageLabels();
  const mapRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<
    'deactivate' | 'activate' | 'active' | 'inactive'
  >(target.active === false ? 'activate' : 'deactivate');

  const handleStatusToggle = () => {
    setStatus((s) => {
      if (s === 'deactivate') return 'active';
      if (s === 'activate') return 'inactive';
      if (s === 'active') return 'inactive';
      return 'active';
    });
  };

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!mapRef.current) return;

    let marker: google.maps.Marker | null = null;
    let circle: google.maps.Circle | null = null;

    loadGoogleMaps(apiKey)
      .then(() => {
        if (!mapRef.current || !window.google?.maps) return;

        const map = new window.google.maps.Map(mapRef.current, {
          center: target.center,
          zoom: radiusToZoom(target.radiusMiles),
          mapTypeControl: false,
          streetViewControl: false,
          cameraControl: false,
          rotateControl: false,
          scaleControl: false,
          fullscreenControl: true,
          zoomControl: true,
        } as google.maps.MapOptions & { cameraControl?: boolean });

        marker = new window.google.maps.Marker({
          position: target.center,
          map,
          clickable: false,
        } as google.maps.MarkerOptions);

        circle = new window.google.maps.Circle({
          center: target.center,
          radius: target.radiusMiles * 1609.34,
          fillColor: '#d21034',
          fillOpacity: 0.4,
          strokeWeight: 0,
          clickable: false,
          map,
        });
      })
      .catch(() => {});

    return () => {
      marker?.setMap(null);
      circle?.setMap(null);
    };
  }, [target]);

  return (
    <AreaCard>
      <MiniMapContainer ref={mapRef} />
      <AreaFooter>
        <AreaTitleRow>
          <AreaName>{target.locationName}</AreaName>
          <AreaActions>
            <ActionLink type="button" onClick={onEdit}>
              {t('actions.edit')}
            </ActionLink>
            <ActionDivider>|</ActionDivider>
            <ActionLink type="button" onClick={handleStatusToggle}>
              {status === 'deactivate'
                ? t('wbs.deactivate')
                : status === 'activate'
                  ? t('wbs.activate')
                  : status === 'active'
                    ? t('wbs.active')
                    : t('wbs.inactive')}
            </ActionLink>
            <ActionDivider>|</ActionDivider>
            <ActionLink
              type="button"
              onClick={() => {
                // Translation Needed
                // TODO: call delete API on confirm
                window.confirm(
                  'Are you sure you want to delete this web ads target?',
                );
              }}
            >
              {t('actions.delete')}
            </ActionLink>
          </AreaActions>
        </AreaTitleRow>
        <DetailRow>
          <DetailText>
            {/* Translations Needed */}
            <span>Radius:</span> {target.radiusMiles} Miles
          </DetailText>
          <Pipe>|</Pipe>
          <DetailText>
            <span>{t('wbs.languages')}:</span>{' '}
            {target.languages.map((l) => languageLabels[l]).join(', ')}
          </DetailText>
          <Pipe>|</Pipe>
          <DetailText>
            {/* Translation Needed */}
            <span>Student Type:</span> {target.studentType}
          </DetailText>
        </DetailRow>
      </AreaFooter>
    </AreaCard>
  );
}

const AreaCard = styled.div`
  border-radius: 5px;
  box-shadow: var(--shadow-md);
  overflow: hidden;
`;

const MiniMapContainer = styled.div`
  height: 200px;
  width: 100%;
`;

const AreaFooter = styled.div`
  background: var(--white);
  box-shadow: inset -0.2px 0.5px 3.2px rgba(34, 34, 34, 0.18);
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px 12px;
`;

const AreaTitleRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const AreaName = styled.span`
  color: var(--grey-blue-400);
  display: inline-flex;
  flex-direction: column;
  font-family: var(--raleway);
  font-size: 16px;

  &::after {
    background: var(--wbs-red);
    content: '';
    display: block;
    height: 4.5px;
    margin-top: 6px;
    width: 60px;
  }
`;

const AreaActions = styled.div`
  align-items: center;
  color: var(--grey-900);
  display: flex;
  gap: 10px;
`;

const ActionLink = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: 12px;
  letter-spacing: 0.2px;
  padding: 0;

  &:hover {
    text-decoration: none;
  }
`;

const ActionDivider = styled.span`
  color: inherit;
  font-size: 12px;
  user-select: none;
`;

const DetailRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 11px;
  margin-top: 2px;
`;

const DetailText = styled.span`
  color: var(--grey-600);
  font-size: 12px;
`;

const Pipe = styled.span`
  color: var(--grey-450);
  font-size: 12px;
  user-select: none;
`;
